import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatDate } from '../lib/orgTimezone';
import { Badge, Button } from '../atoms';
import { StatCard, StatCardRow } from '../organisms';
import { QueryState, SectionTitle, fileToBase64 } from './primitives';

/**
 * RequestsAccordion (portal #39 + #38) — the client's single "here's what we need
 * from you" surface. It is:
 *   • a short ONBOARDING checklist derived from the client's own portal state
 *     (set up a signature, respond to open requests) — a lightweight getting-started
 *     nudge, no extra backend; and
 *   • a collapsible list of the firm's OPEN information/document requests
 *     (GET /api/client-portal/information-requests). Each request expands to its
 *     item checklist; an OUTSTANDING item on a document request carries an inline
 *     "Upload" affordance that fulfils it (POST /api/client-portal/documents with
 *     requested_item_id) — the client sends the exact document the firm asked for
 *     and the item flips to received, advancing the request sent → partial → received.
 */

interface RequestedItem {
  id: string; description: string; category: string | null;
  received: boolean; received_date: string | null;
}
interface InfoRequest {
  id: string; title: string; type: string | null; status: string;
  tax_year: number | null; due_date: string | null; property_name: string | null;
  is_overdue: boolean; items: RequestedItem[];
  items_total: number; items_received: number; items_outstanding: number;
}
interface SavedSignature { id: string }

const dt = (s?: string | null) => formatDate(s);

const REQUEST_STATUS_VARIANT: Record<string, 'success' | 'warning' | 'info' | 'neutral'> = {
  complete: 'success', received: 'success', partial: 'warning', sent: 'info',
};

export function RequestsAccordion({ onNavigate }: { onNavigate?: (tab: string) => void }) {
  const q = useQuery({
    queryKey: ['cp', 'information-requests'],
    queryFn: ({ signal }) => api.get<InfoRequest[]>('/api/client-portal/information-requests', signal),
  });
  const sig = useQuery({
    queryKey: ['cp', 'signature'],
    queryFn: ({ signal }) => api.get<{ signature: SavedSignature | null }>('/api/client-portal/signature', signal),
  });

  const rows = q.data ?? [];
  // "Open" == still awaiting the client (sent/partial) — matches the Overview
  // card's open_requests count, which the server computes as status IN
  // ('sent','partial'). rows also includes received/complete, so counting
  // rows.length here over-reported vs the Overview under the same label.
  const openRequests = rows.filter((r) => r.status === 'sent' || r.status === 'partial').length;
  const itemsOutstanding = rows.reduce((n, r) => n + (r.items_outstanding || 0), 0);
  const hasSignature = !!sig.data?.signature;

  // Onboarding steps derived from the client's own portal state (no extra backend).
  const steps = [
    {
      key: 'signature',
      label: 'Set up your signature',
      hint: 'Draw it once — we reuse it on forms and authorizations.',
      done: hasSignature,
      action: onNavigate ? { label: hasSignature ? 'View' : 'Set up', go: 'signature' } : null,
    },
    {
      key: 'requests',
      label: 'Respond to open requests',
      hint: itemsOutstanding > 0
        ? `${itemsOutstanding} item${itemsOutstanding === 1 ? '' : 's'} still outstanding — upload them below.`
        : 'You’re all caught up on what we’ve asked for.',
      done: itemsOutstanding === 0,
      action: null,
    },
  ];

  return (
    <div>
      <SectionTitle>What we need from you</SectionTitle>
      <div style={{ fontSize: 13, color: 'var(--color-muted, #666)', margin: '0 0 16px' }}>
        Your onboarding steps and any documents your team has requested. Upload an item and it goes straight to your team.
      </div>

      <StatCardRow>
        <StatCard label="Open requests" value={openRequests} />
        <StatCard label="Items outstanding" value={itemsOutstanding} accent={itemsOutstanding > 0 ? 'amber' : undefined} />
      </StatCardRow>

      {/* Onboarding checklist */}
      <div style={{ margin: '20px 0' }}>
        <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-muted, #666)', marginBottom: 8 }}>
          Getting started
        </div>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, border: '1px solid var(--color-border, #e2e2df)', borderRadius: 10, background: 'var(--color-surface-raised, #fff)' }}>
          {steps.map((s, i) => (
            <li key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderTop: i ? '1px solid var(--color-border-subtle, #f0f0ee)' : 'none' }}>
              <span aria-hidden="true" style={{ color: s.done ? 'var(--color-status-green, #14532d)' : 'var(--color-muted, #999)', fontWeight: 700, fontSize: 16, lineHeight: 1 }}>
                {s.done ? '✓' : '○'}
              </span>
              <div style={{ marginRight: 'auto' }}>
                <div style={{ fontSize: 14, fontWeight: 600, textDecoration: s.done ? 'line-through' : 'none', color: s.done ? 'var(--color-muted, #666)' : 'var(--color-ink, #1a1a1a)' }}>{s.label}</div>
                <div style={{ fontSize: 12, color: 'var(--color-muted, #666)' }}>{s.hint}</div>
              </div>
              {s.action && (
                <Button size="sm" variant="secondary" onClick={() => onNavigate?.(s.action!.go)}>{s.action.label}</Button>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Open requests, collapsible */}
      <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-muted, #666)', marginBottom: 8 }}>
        Requests
      </div>
      <QueryState isLoading={q.isLoading} error={q.error} isEmpty={rows.length === 0} emptyLabel="No open requests — nothing needed from you right now.">
        <div style={{ display: 'grid', gap: 12 }}>
          {rows.map((r) => (
            <RequestCard
              key={r.id}
              request={r}
              onGoToDocuments={onNavigate ? () => onNavigate('documents') : undefined}
            />
          ))}
        </div>
      </QueryState>
    </div>
  );
}

function RequestCard({ request, onGoToDocuments }: { request: InfoRequest; onGoToDocuments?: () => void }) {
  // Default-open when anything is still outstanding, so the client sees what to do.
  const [open, setOpen] = useState(request.items_outstanding > 0);
  const isDocRequest = request.type === 'document_request';
  return (
    <div style={{ border: '1px solid var(--color-border, #e2e2df)', borderRadius: 10, background: 'var(--color-surface-raised, #fff)', overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          appearance: 'none', border: 'none', background: 'none', cursor: 'pointer', width: '100%', textAlign: 'left',
          display: 'flex', alignItems: 'center', gap: 10, padding: 16,
        }}
      >
        <span aria-hidden="true" style={{ fontSize: 12, color: 'var(--color-muted, #666)', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>▶</span>
        <div style={{ marginRight: 'auto', minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{request.title}</div>
          <div style={{ fontSize: 12, color: 'var(--color-muted, #666)', display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 2 }}>
            {isDocRequest && <span>Document request</span>}
            {request.property_name && <span>{request.property_name}</span>}
            {request.due_date && <span>Due {dt(request.due_date)}</span>}
            <span>{request.items_received} of {request.items_total} received</span>
          </div>
        </div>
        {request.is_overdue && <Badge variant="warning">Overdue</Badge>}
        <Badge variant={REQUEST_STATUS_VARIANT[request.status] ?? 'neutral'}>{request.status}</Badge>
      </button>

      {open && (
        <ul style={{ listStyle: 'none', padding: '0 16px 12px', margin: 0 }}>
          {request.items.length === 0 && (
            <li style={{ fontSize: 13, color: 'var(--color-muted, #666)', padding: '4px 0 8px' }}>No individual items listed.</li>
          )}
          {request.items.map((it) => (
            <ItemRow key={it.id} item={it} allowUpload={isDocRequest} onGoToDocuments={onGoToDocuments} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ItemRow({ item, allowUpload, onGoToDocuments }: { item: RequestedItem; allowUpload: boolean; onGoToDocuments?: () => void }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const mut = useMutation({
    mutationFn: async (file: File) => {
      const file_data = await fileToBase64(file);
      // No document_type: this upload always carries a requested_item_id, so the
      // server stamps the FIRM's requested type on the stored document (migration 121,
      // E4) and ignores any client-sent type. Sending one here would be dead.
      return api.post('/api/client-portal/documents', {
        name: item.description || file.name,
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        file_data,
        requested_item_id: item.id,
      });
    },
    onSuccess: () => {
      // Fulfilling flips the item + advances the request; refresh both surfaces + the overview.
      qc.invalidateQueries({ queryKey: ['cp', 'information-requests'] });
      qc.invalidateQueries({ queryKey: ['cp', 'documents'] });
      qc.invalidateQueries({ queryKey: ['cp', 'summary'] });
    },
  });

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) mut.mutate(f);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <li style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid var(--color-border-subtle, #f0f0ee)' }}>
      <span aria-hidden="true" style={{ color: item.received ? 'var(--color-status-green, #14532d)' : 'var(--color-muted, #999)', fontWeight: 700, lineHeight: 1.4 }}>
        {item.received ? '✓' : '○'}
      </span>
      <span style={{ flex: 1, minWidth: 0, fontSize: 14, color: item.received ? 'var(--color-muted, #666)' : 'var(--color-ink, #1a1a1a)', textDecoration: item.received ? 'line-through' : 'none' }}>
        {item.description}
      </span>
      {item.received ? (
        <span style={{ fontSize: 12, color: 'var(--color-status-green, #14532d)' }}>Received{item.received_date ? ` ${dt(item.received_date)}` : ''}</span>
      ) : allowUpload ? (
        <>
          <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={onPick} aria-label={`Upload ${item.description}`} />
          <Button size="sm" loading={mut.isPending} onClick={() => fileRef.current?.click()}>Upload</Button>
        </>
      ) : onGoToDocuments ? (
        // Non-document requests can't be fulfilled inline — send the client to the
        // Documents tab where they can upload. Same wording, now an actionable link.
        <button
          type="button"
          onClick={onGoToDocuments}
          style={{
            appearance: 'none', background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            fontSize: 12, color: 'var(--brand-primary, #1D4E45)', fontWeight: 600, textDecoration: 'underline', fontFamily: 'inherit',
          }}
        >Send via the Documents tab</button>
      ) : (
        <span style={{ fontSize: 12, color: 'var(--color-muted, #666)' }}>Send via the Documents tab</span>
      )}
      {mut.isError && <span style={{ fontSize: 12, color: 'var(--color-status-red, #b00)' }}>Upload failed</span>}
    </li>
  );
}
